import { getLogger } from '../framework/logging/logger';
import { isDogfoodEnv } from 'owa-metatags';

// Time from loading scripts till performance numbers are logged.
const logTime = 30000;

const perfApiAvailable = !!(
    window.performance?.timing &&
    window.performance.mark &&
    window.performance.getEntriesByName
);

type BootstrapLifecycleEvent =
    | 'Start'
    | 'SessionReturned'
    | 'UserSettingsReturned'
    | 'StringsReturned'
    | 'AppComponentPrerender'
    | 'AppComponentPostRender';

type PLTLifecycleEvent =
    | 'BootstrapQueriesStart'
    | 'FoldersQueryCompleted'
    | 'ContactsQueryCompleted'
    | 'FavoritesQueryCompleted'
    | 'BootstrapQueriesCompleted'
    | 'PeopleHubResolved'
    | 'PeopleHubMounted'
    | 'PersonaListFirstReceivesData'
    | 'LPCLoadInitiated';

type PLTLifecycleType = 'PeopleHubExperienceType';

type PLTExperienceType = 'Contacts' | 'Favorites' | 'Unknown';

const getPrefixedBootstrapMarkName = (markName: BootstrapLifecycleEvent) =>
    'PeopleHub_Bootstrap_' + markName;
const getPrefixedPLTMarkName = (markName: PLTLifecycleEvent) => 'PeopleHub_PLT_' + markName;

// Returns timing of mark as milliseconds from NavigationStart event
const getBootstrapTiming = (event: BootstrapLifecycleEvent) => {
    try {
        return (
            perfApiAvailable &&
            window.performance
                .getEntriesByName(getPrefixedBootstrapMarkName(event))[0]
                .startTime.toString()
        );
    } catch {
        return undefined;
    }
};

const getPLTTiming = (event: PLTLifecycleEvent) => {
    try {
        return (
            perfApiAvailable &&
            window.performance
                .getEntriesByName(getPrefixedPLTMarkName(event))[0]
                .startTime.toString()
        );
    } catch {
        return undefined;
    }
};

const pltMarkExists = (event: PLTLifecycleEvent): boolean =>
    !!(
        perfApiAvailable &&
        window.performance.getEntriesByName(getPrefixedPLTMarkName(event)).length
    );

const getTimeToFirstByte = () =>
    perfApiAvailable &&
    (
        window.performance.timing.responseStart - window.performance.timing.navigationStart
    ).toString();

const getAuthRedirect = () =>
    perfApiAvailable &&
    (window.performance.timing.fetchStart - window.performance.timing.navigationStart).toString();

type ResourceStats = {
    name: string;
    fetchStart: number;
    responseEnd: number;
    encodedSize: number;
    decodedSize: number;
    hasTimings: boolean;
};
const getBundleTimings = (scriptName: string): ResourceStats => {
    if (!perfApiAvailable) {
        return {} as ResourceStats;
    }

    const scripts = window.performance
        .getEntriesByType('resource')
        .filter(e => e.name.indexOf(scriptName) > 0);
    const peopleScriptMark: any = scripts?.[0];

    return {
        name: scriptName,
        fetchStart: peopleScriptMark ? peopleScriptMark.fetchStart : 0,
        responseEnd: peopleScriptMark ? peopleScriptMark.responseEnd : 0,
        encodedSize: peopleScriptMark ? peopleScriptMark.encodedBodySize : 0,
        decodedSize: peopleScriptMark ? peopleScriptMark.decodedBodySize : 0,
        hasTimings: !!peopleScriptMark,
    };
};

const logEventIfSDF = (event: BootstrapLifecycleEvent | PLTLifecycleEvent) => {
    if (isDogfoodEnv()) {
        getLogger().log(`LifecycleEvent.${event}`);
    }
};

const markPLTEventInternal = (event: PLTLifecycleEvent): void => {
    logEventIfSDF(event);
    return perfApiAvailable && window.performance.mark(getPrefixedPLTMarkName(event));
};

export const markBootstrapEvent = (event: BootstrapLifecycleEvent) => {
    logEventIfSDF(event);
    return perfApiAvailable && window.performance.mark(getPrefixedBootstrapMarkName(event));
};

type StringDict = { [key: string]: string };
interface BootstrapMetrics extends StringDict {
    apiAvailable: string;
    ttfb: string;
    authRedirect: string;
    bootstrapStart: string;
    sessionReturned: string;
    userSettingsReturned: string;
    stringsReturned: string;
    appComponentPrerender: string;
    appComponentPostRender: string;
    peopleBundleTimings: string;
}

const getBootstrapTimings = (): BootstrapMetrics => {
    return {
        apiAvailable: perfApiAvailable.toString(),
        ttfb: getTimeToFirstByte(),
        authRedirect: getAuthRedirect(),
        bootstrapStart: getBootstrapTiming('Start'),
        sessionReturned: getBootstrapTiming('SessionReturned'),
        userSettingsReturned: getBootstrapTiming('UserSettingsReturned'),
        stringsReturned: getBootstrapTiming('StringsReturned'),
        appComponentPrerender: getBootstrapTiming('AppComponentPrerender'),
        appComponentPostRender: getBootstrapTiming('AppComponentPostRender'),
        peopleBundleTimings: JSON.stringify(getBundleTimings('owa.people.js')),
    };
};
export const logBootstrapTimings = () => {
    getLogger().log('PeopleHubBootstrapTimings', getBootstrapTimings());
};

interface PLTMetrics extends BootstrapMetrics {
    personaListFirstReceivesData: string;
    bootstrapQueriesStart: string;
    contactsQueryCompleted: string;
    foldersQueryCompleted: string;
    favoritesQueryCompleted: string;
    bootstrapQueriesCompleted: string;
    peopleHubBundleTimings: string;
    peopleHubResolved: string;
    peopleHubMounted: string;
    pltTimestamp: string;
    lpcLoadInitiated: string;
    experienceType: PLTExperienceType;
}

const getPLTTimings = (experienceType: PLTExperienceType): PLTMetrics => {
    return {
        ...getBootstrapTimings(),
        personaListFirstReceivesData: getPLTTiming('PersonaListFirstReceivesData'),
        bootstrapQueriesStart: getPLTTiming('BootstrapQueriesStart'),
        contactsQueryCompleted: getPLTTiming('ContactsQueryCompleted'),
        foldersQueryCompleted: getPLTTiming('FoldersQueryCompleted'),
        favoritesQueryCompleted: getPLTTiming('FavoritesQueryCompleted'),
        bootstrapQueriesCompleted: getPLTTiming('BootstrapQueriesCompleted'),
        peopleHubBundleTimings: JSON.stringify(getBundleTimings('owa.PeopleHubBundle.js')),
        peopleHubResolved: getPLTTiming('PeopleHubResolved'),
        peopleHubMounted: getPLTTiming('PeopleHubMounted'),
        lpcLoadInitiated: getPLTTiming('LPCLoadInitiated'),
        pltTimestamp: Date.now().toString(),
        experienceType,
    };
};

const globalVariablePrefix = '__PeopleHubPltLogged';
const getGlobalVariableName = (eventName: PLTLifecycleEvent | PLTLifecycleType): string =>
    `${globalVariablePrefix}_${eventName}`;

export const markPLTEvent = (eventName: PLTLifecycleEvent): boolean => {
    const globalVariableName = getGlobalVariableName(eventName);
    if (pltMarkExists(eventName) || window[globalVariableName]) {
        return false;
    }

    markPLTEventInternal(eventName);
    window[globalVariableName] = true;
    return true;
};

export const markPLTType = (eventName: PLTLifecycleType, value: PLTExperienceType): boolean => {
    const globalVariableName = getGlobalVariableName(eventName);
    if (window[globalVariableName]) {
        return false;
    }

    window[globalVariableName] = value;
    return false;
};

const getExperienceType = (): PLTExperienceType => {
    const globalVariableName = getGlobalVariableName('PeopleHubExperienceType');
    return window[globalVariableName] ? window[globalVariableName] : 'Unknown';
};

setTimeout(() => {
    getLogger().log('PeopleHubPLTTimings', getPLTTimings(getExperienceType()));
}, logTime);
